import { useCallback } from 'react';

import { useSelector } from 'react-redux';

import {
  TestCaseAttribute,
  ExecutionStrategy,
} from 'src/components/pipelines/components/test-management/types';
import envBaseUrl from 'src/components/pipelines/utils/env-base-url';
import { getCurrentRepositoryFullSlug } from 'src/selectors/repository-selectors';
import { useMutation } from 'src/utils/fb-query';
import { authRequest, jsonHeaders } from 'src/utils/fetch';
import { captureExceptionWithTags } from 'src/utils/sentry';

export interface UpdateTestCaseStatusParams {
  testUuid: string;
  attributes?: TestCaseAttribute[];
  executionStrategy?: ExecutionStrategy;
}

const putTestCaseStatus = async (
  repositoryFullSlug: string,
  { testUuid, attributes, executionStrategy }: UpdateTestCaseStatusParams
) => {
  const updateUrl = `${envBaseUrl(
    '/!api/internal'
  )}/repositories/${repositoryFullSlug}/tests/${testUuid}`;

  const body = JSON.stringify({
    ...(attributes !== undefined && { attributes }),
    ...(executionStrategy !== undefined && {
      execution_strategy: executionStrategy,
    }),
  });

  const response = await fetch(
    authRequest(updateUrl, {
      method: 'PUT',
      headers: jsonHeaders,
      body,
    })
  );

  if (!response.ok) {
    throw new Error(
      `Failed to update test case status: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

export const useUpdateTestCaseStatus = (reloadTestCases: () => void) => {
  const repositoryFullSlug = useSelector(getCurrentRepositoryFullSlug);

  const update = useCallback(
    (params: UpdateTestCaseStatusParams) => {
      if (
        !repositoryFullSlug ||
        !params.testUuid ||
        (params?.attributes === undefined &&
          params?.executionStrategy === undefined)
      ) {
        throw new Error('Data required to update test case status is missing');
      }

      return putTestCaseStatus(repositoryFullSlug, params);
    },
    [repositoryFullSlug]
  );

  const { mutate: updateTestCaseStatus, isPending: isTestCaseUpdating } =
    useMutation(update, {
      onSuccess: () => {
        reloadTestCases();
      },
      onError: (error: Error) => {
        captureExceptionWithTags(error, {
          section: 'test-management',
          action: 'update-test-case-status',
        });
      },
    });

  return { updateTestCaseStatus, isTestCaseUpdating };
};
